import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import CompanyHeader from '../../components/companies/CompanyHeader';
import ClinicDrawer from '../../components/companies/ClinicDrawer';
import StatusBadge from '../../components/crm/StatusBadge';
import KpiCard from '../../components/crm/KpiCard';
import Sparkline from '../../components/crm/Sparkline';
import {
  getCompany,
  getCompanyClinics,
  getCompanyContracts,
  getCompanyActivities,
  getCompanyFiles,
  addClinic,
  uploadCompanyFile
} from '../../services/mock/companyService';
import { ActivityTypes, ClinicStatuses } from '../../types/crm';

const CompanyDetail = () => {
  const { org } = useOrg();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [company, setCompany] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Clinic-related state
  const [clinicSearch, setClinicSearch] = useState('');
  const [clinicStatusFilter, setClinicStatusFilter] = useState('all');
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [clinicDrawerOpen, setClinicDrawerOpen] = useState(false);
  const [addClinicModalOpen, setAddClinicModalOpen] = useState(false);
  
  // Activity filter state
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');
  const [activityDateRange, setActivityDateRange] = useState('30d');
  
  // New clinic form
  const [newClinicForm, setNewClinicForm] = useState({
    name: '',
    city: '',
    state: '',
    npi: ''
  });

  const showGMV = process.env.REACT_APP_SHOW_GMV === 'true';

  useEffect(() => {
    loadCompanyData();
  }, [org, companyId]);

  useEffect(() => {
    // Determine tab from URL
    const pathname = location.pathname;
    if (pathname.includes('/clinics')) setActiveTab(1);
    else if (pathname.includes('/contracts')) setActiveTab(2);
    else if (pathname.includes('/activity')) setActiveTab(3);
    else if (pathname.includes('/files')) setActiveTab(4);
    else setActiveTab(0);
  }, [location.pathname]);

  const loadCompanyData = async () => {
    setLoading(true);
    try {
      const [companyData, clinicsData, contractsData, activitiesData, filesData] = 
        await Promise.all([
          getCompany(org, companyId),
          getCompanyClinics(org, companyId),
          getCompanyContracts(org, companyId),
          getCompanyActivities(org, companyId),
          getCompanyFiles(org, companyId)
        ]);

      setCompany(companyData);
      setClinics(clinicsData);
      setContracts(contractsData);
      setActivities(activitiesData);
      setFiles(filesData);
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const tabs = ['overview', 'clinics', 'contracts', 'activity', 'files'];
    navigate(`/internal/${org}/companies/${companyId}/${tabs[newValue]}`);
  };

  const handleRepChange = (newRep) => {
    setCompany(prev => ({
      ...prev,
      repId: newRep.id,
      repName: newRep.name
    }));
  };

  const handleOpenClinic = (clinic) => {
    setSelectedClinic(clinic);
    setClinicDrawerOpen(true);
  };

  const handleAddClinic = async () => {
    try {
      const newClinic = await addClinic(org, companyId, newClinicForm);
      setClinics([...clinics, newClinic]);
      setAddClinicModalOpen(false);
      setNewClinicForm({ name: '', city: '', state: '', npi: '' });
    } catch (error) {
      console.error('Error adding clinic:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const uploadedFile = await uploadCompanyFile(org, companyId, file);
        setFiles([...files, uploadedFile]);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
  };

  const filteredClinics = clinics.filter(clinic => {
    const matchesSearch = clinic.name.toLowerCase().includes(clinicSearch.toLowerCase()) ||
                         `${clinic.city}, ${clinic.state}`.toLowerCase().includes(clinicSearch.toLowerCase());
    const matchesStatus = clinicStatusFilter === 'all' || clinic.status === clinicStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredActivities = activities.filter(activity => 
    activityTypeFilter === 'all' || activity.type === activityTypeFilter
  );

  const formatCurrency = (value) => `$${value?.toLocaleString() || 0}`;
  
  // Generate mock trend data
  const revenueData = Array.from({ length: 12 }, () => Math.floor(8000 + Math.random() * 4000));
  const ordersData = Array.from({ length: 12 }, () => Math.floor(3 + Math.random() * 8));

  if (loading || !company) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  return (
    <Box>
      <Breadcrumbs />
      
      {/* Company Header */}
      <CompanyHeader 
        company={company} 
        org={org} 
        onRepChange={handleRepChange} 
      />

      {/* Tabs */}
      <Card sx={{ border: '1px solid #e2e8f0' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          textColor="primary"
          indicatorColor="primary"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider', 
            px: { xs: 1, md: 3 },
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: { xs: 2, md: 3 },
              fontSize: { xs: '0.92rem', md: '1rem' },
              textTransform: 'none',
              whiteSpace: 'nowrap',
            },
            '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.35 },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Clinics" />
          <Tab label="Contracts" />
          <Tab label="Activity" />
          <Tab label="Files" />
        </Tabs>

        <CardContent sx={{ p: 3 }}>
          {/* Overview Tab */}
          {activeTab === 0 && (
            <Box>
              {/* Metric Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {showGMV && (
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      title="Revenue 12m"
                      value={company.revenue12m}
                      icon={<TrendingUpIcon />}
                      color={org === 'bare' ? '#5c725c' : '#0d9488'}
                      formatter={formatCurrency}
                      trend="up"
                      trendValue={8}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={showGMV ? 3 : 4}>
                  <KpiCard
                    title="Orders 12m"
                    value={company.orders12m}
                    icon={<AssignmentIcon />}
                    color="#f59e0b"
                  />
                </Grid>
                {showGMV && (
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      title="Avg Order Value"
                      value={company.aov12m}
                      icon={<TimelineIcon />}
                      color="#8b5cf6"
                      formatter={formatCurrency}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={showGMV ? 3 : 4}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    Last Order
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#0f172a' }}>
                    {company.lastOrder ? new Date(company.lastOrder).toLocaleDateString() : 'No recent orders'}
                  </Typography>
                  {company.lastOrder && (
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {Math.floor((Date.now() - new Date(company.lastOrder)) / (1000 * 60 * 60 * 24))} days ago
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                {/* Revenue/Units Trend */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ border: '1px solid #e2e8f0', height: '300px' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {showGMV ? 'Revenue' : 'Orders'} Trend (12m)
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Sparkline 
                          data={showGMV ? revenueData : ordersData} 
                          width={200} 
                          height={60}
                          color={org === 'bare' ? '#5c725c' : '#0d9488'}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {showGMV 
                          ? `Avg: ${formatCurrency(revenueData.reduce((a, b) => a + b) / revenueData.length)}/month`
                          : `Avg: ${Math.round(ordersData.reduce((a, b) => a + b) / ordersData.length)} orders/month`
                        }
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Top Clinics */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ border: '1px solid #e2e8f0', height: '300px' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Top Clinics (90d)
                      </Typography>
                      <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {clinics.slice(0, 5).map(clinic => (
                          <Box 
                            key={clinic.id} 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              py: 1,
                              borderBottom: '1px solid #f1f5f9',
                              '&:last-child': { borderBottom: 'none' }
                            }}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {clinic.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {clinic.lastOrder || 'No recent orders'}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {clinic.units90d}
                              </Typography>
                              <StatusBadge status={clinic.status} size="small" />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Health Signals */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ border: '1px solid #e2e8f0', height: '300px' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Health Signals
                      </Typography>
                      <Box>
                        {company.status === 'At Risk' && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            Account flagged as at-risk
                          </Alert>
                        )}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                            No activity in 21 days
                          </Typography>
                        </Box>
                        {contracts.some(c => c.status === 'Expiring') && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
                              Contract expires in 45 days
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
                            All quality checks passed
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Clinics Tab */}
          {activeTab === 1 && (
            <Box>
              {/* Search and Filters */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search clinics..."
                  value={clinicSearch}
                  onChange={(e) => setClinicSearch(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={clinicStatusFilter}
                    onChange={(e) => setClinicStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All</MenuItem>
                    {Object.values(ClinicStatuses).map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddClinicModalOpen(true)}
                  sx={{
                    bgcolor: org === 'bare' ? '#5c725c' : '#0d9488',
                    '&:hover': {
                      bgcolor: org === 'bare' ? '#4a5d4a' : '#0b7a6e'
                    }
                  }}
                >
                  Add Clinic
                </Button>
              </Box>

              {/* Clinics Table / Mobile List */}
              {isMobile ? (
                <Stack spacing={1.5} sx={{ p: 1 }}>
                  {filteredClinics.map((clinic) => (
                    <Paper key={clinic.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{clinic.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {clinic.city}, {clinic.state}
                          </Typography>
                        </Box>
                        <StatusBadge status={clinic.status} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Units: {clinic.units30d} / {clinic.units90d}</Typography>
                        <Typography variant="body2" color="text.secondary">Last: {clinic.lastOrder || '—'}</Typography>
                        <Typography variant="body2" color="text.secondary">Contacts: {clinic.contacts}</Typography>
                      </Box>
                      <Button size="small" variant="contained" onClick={() => handleOpenClinic(clinic)}>
                        Open Clinic
                      </Button>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Clinic Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Units (30/90d)</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Last Order</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Contacts</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredClinics.map(clinic => (
                        <TableRow key={clinic.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{clinic.name}</TableCell>
                          <TableCell>{clinic.city}, {clinic.state}</TableCell>
                          <TableCell>{clinic.units30d} / {clinic.units90d}</TableCell>
                          <TableCell>{clinic.lastOrder || 'No recent orders'}</TableCell>
                          <TableCell>{clinic.contacts}</TableCell>
                          <TableCell>
                            <StatusBadge status={clinic.status} />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenClinic(clinic)}
                            >
                              Open Clinic
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Contracts Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Contracts
              </Typography>
              
              {contracts.some(c => c.status === 'Expiring') && (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 3 }}
                  icon={<WarningIcon />}
                >
                  One or more contracts expire within 60 days. Consider renewal discussions.
                </Alert>
              )}

              {isMobile ? (
                <Stack spacing={1.5} sx={{ p: 1 }}>
                  {contracts.map((contract) => (
                    <Paper key={contract.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{contract.id}</Typography>
                        <StatusBadge status={contract.status} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Start: {contract.start}</Typography>
                        <Typography variant="body2" color="text.secondary">End: {contract.end}</Typography>
                        <Typography variant="body2" color="text.secondary">Terms: {contract.terms}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />}>Download</Button>
                        <Button size="small" variant="contained">Replace</Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Contract #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Terms</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contracts.map(contract => (
                      <TableRow key={contract.id} hover>
                        <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {contract.id}
                        </TableCell>
                        <TableCell>{contract.start}</TableCell>
                        <TableCell>{contract.end}</TableCell>
                        <TableCell>{contract.terms}</TableCell>
                        <TableCell>
                          <StatusBadge status={contract.status} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton size="small">
                              <DownloadIcon />
                            </IconButton>
                            <Button size="small" variant="outlined">
                              Replace
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Activity Tab */}
          {activeTab === 3 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Activity Type</InputLabel>
                  <Select
                    value={activityTypeFilter}
                    onChange={(e) => setActivityTypeFilter(e.target.value)}
                    label="Activity Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    {Object.values(ActivityTypes).map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={activityDateRange}
                    onChange={(e) => setActivityDateRange(e.target.value)}
                    label="Date Range"
                  >
                    <MenuItem value="7d">Last 7 days</MenuItem>
                    <MenuItem value="30d">Last 30 days</MenuItem>
                    <MenuItem value="90d">Last 90 days</MenuItem>
                  </Select>
                </FormControl>
                <Button sx={{ whiteSpace: 'nowrap' }} variant="contained" startIcon={<AddIcon />}>
                  Log Activity
                </Button>
              </Box>

              <Box sx={{ maxHeight: '600px', overflowY: 'auto' }}>
                {filteredActivities.map(activity => (
                  <Box 
                    key={activity.id} 
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      border: '1px solid #e2e8f0', 
                      borderRadius: 1,
                      bgcolor: '#f8fafc'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <StatusBadge status={activity.type} />
                        <StatusBadge status={activity.outcome} variant="outlined" />
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {new Date(activity.ts).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#374151' }}>
                      {activity.note}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Files Tab */}
          {activeTab === 4 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Files & Documents
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  component="label"
                  sx={{
                    bgcolor: org === 'bare' ? '#5c725c' : '#0d9488',
                    '&:hover': {
                      bgcolor: org === 'bare' ? '#4a5d4a' : '#0b7a6e'
                    }
                  }}
                >
                  Upload File
                  <input
                    type="file"
                    hidden
                    onChange={handleFileUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                </Button>
              </Box>

              {isMobile ? (
                <Stack spacing={1.5} sx={{ p: 1 }}>
                  {files.map((file) => (
                    <Paper key={file.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                        <Chip label={file.type.toUpperCase()} size="small" />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Size: {file.sizeKb} KB</Typography>
                        <Typography variant="body2" color="text.secondary">Uploaded: {new Date(file.uploadedAt).toLocaleDateString()}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />}>Download</Button>
                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}>Delete</Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {files.map(file => (
                      <TableRow key={file.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{file.name}</TableCell>
                        <TableCell>
                          <Chip label={file.type.toUpperCase()} size="small" />
                        </TableCell>
                        <TableCell>{file.sizeKb} KB</TableCell>
                        <TableCell>{new Date(file.uploadedAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton size="small">
                              <DownloadIcon />
                            </IconButton>
                            <IconButton size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Clinic Modal */}
      <Dialog open={addClinicModalOpen} onClose={() => setAddClinicModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Clinic</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Clinic Name"
                value={newClinicForm.name}
                onChange={(e) => setNewClinicForm({...newClinicForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="City"
                value={newClinicForm.city}
                onChange={(e) => setNewClinicForm({...newClinicForm, city: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={newClinicForm.state}
                onChange={(e) => setNewClinicForm({...newClinicForm, state: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="NPI (Optional)"
                value={newClinicForm.npi}
                onChange={(e) => setNewClinicForm({...newClinicForm, npi: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddClinicModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddClinic}
            disabled={!newClinicForm.name || !newClinicForm.city || !newClinicForm.state}
            sx={{
              bgcolor: org === 'bare' ? '#5c725c' : '#0d9488',
              '&:hover': {
                bgcolor: org === 'bare' ? '#4a5d4a' : '#0b7a6e'
              }
            }}
          >
            Add Clinic
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clinic Drawer */}
      <ClinicDrawer
        open={clinicDrawerOpen}
        onClose={() => setClinicDrawerOpen(false)}
        clinic={selectedClinic}
        org={org}
      />
    </Box>
  );
};

export default CompanyDetail;
